import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Drawer, message } from "antd";
import { Context, Hooks, Util, Platform, Bridge } from "@xt/xtbase";
import voucherIcon from "assets/images/task/voucher.png";
import checkIcon from "assets/images/task/check.png";
import checkActiveIcon from "assets/images/task/check-active.png";
import {
  getActivityList,
  getActivityListPast,
  joinActivity,
  claimActivityReward,
} from "@/api/activity";
import {
  appendActivityDepositQueryToPath,
  appendTradeMinLeverageToDescription,
  getDepositAmountRuleFromCondition,
} from "@/components/Pages/Home/activityHomeShared";
import { resolveActivityWebJumpPath } from "@/utils/activityDepositJump";
import { fetchUserCouponTotalCount, resolveCouponTenantId } from "@/api/coupon-user";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;
const WEEK_DAYS = ["Wed", "Thu", "Fri", "Sat", "Sun", "Mon", "Tue"];

/** 后端 completed 可能是 boolean / 0|1 / "true"|"false"，避免字符串 "false" 被当成 truthy 导致按钮整颗 disabled */
function isTaskCompleted(tk) {
  if (!tk || tk.completed == null) return false;
  const c = tk.completed;
  if (c === true || c === 1 || c === "1" || c === "true" || c === "TRUE") return true;
  if (c === false || c === 0 || c === "0" || c === "false" || c === "FALSE" || c === "") return false;
  return Boolean(c);
}

/** 无有效目标且当前进度为 0 时不展示 Progress 行（避免 0/0） */
function shouldShowConditionProgressRow(tk) {
  if (!tk) return false;
  const nTgt = Number(tk.progressTarget);
  const nCur = Number(tk.progressCurrent);
  const hasTarget =
    tk.progressTarget != null &&
    tk.progressTarget !== "" &&
    !Number.isNaN(nTgt) &&
    nTgt > 0;
  const hasCurrent =
    tk.progressCurrent != null &&
    tk.progressCurrent !== "" &&
    !Number.isNaN(nCur) &&
    nCur > 0;
  return hasTarget || hasCurrent;
}

/** 任务标题：优先 conditionDescription，其次 conditionName；交易条件可追加 params.minLeverage */
function taskConditionDisplayText(task, t) {
  if (!task) return "";
  const desc = task.conditionDescription;
  const base =
    desc != null && String(desc).trim() !== ""
      ? String(desc).trim()
      : task.conditionName != null && String(task.conditionName).trim() !== ""
        ? String(task.conditionName).trim()
        : "";
  return appendTradeMinLeverageToDescription(base, task, t);
}

/** PWA 主屏打开：不走原生 appRoute */
function isPwaStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone) return true;
  } catch (_) {
    /* ignore */
  }
  return false;
}

/** App 内跳转：接口 jumpUrls 固定小写字段 ios、android */
function resolveTaskNativeJumpPath(task) {
  const ju = task?.jumpUrls;
  if (!ju || typeof ju !== "object") return "";
  const ios = typeof ju.ios === "string" ? ju.ios.trim() : "";
  const android = typeof ju.android === "string" ? ju.android.trim() : "";
  if (Platform.isIOS()) return ios || android;
  if (Platform.isAndroid()) return android || ios;
  return android || ios;
}

/**
 * appRoute 的 path：相对路径需以 / 开头；完整 URL 则只取 pathname+search+hash（查询串一并放在 path）
 */
function normalizeAppRoutePath(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      return `${u.pathname}${u.search}${u.hash}`;
    } catch {
      return s;
    }
  }
  if (!s.startsWith("/")) return `/${s.replace(/^\/+/, "")}`;
  return s;
}

/** 是否走原生 App JSBridge（非 PWA） */
function shouldUseAppRouteForTaskJump() {
  return Platform.isInApp() && !isPwaStandalone();
}

/** 从 task 或 jumpUrls 对象中解析可跳转链接（兼容 web/h5/app 等 key） */
function resolveTaskJumpUrl(task) {
  if (!task) return "";
  const direct = task.jumpUrl || task.jumpLink || task.url;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const ju = task.jumpUrls;
  if (!ju) return "";
  if (typeof ju === "string" && ju.trim()) return ju.trim();
  const preferKeys = ["web", "WEB", "Web", "h5", "H5", "mobile", "MOBILE", "common", "COMMON"];
  for (const k of preferKeys) {
    const v = ju[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (v && typeof v === "object" && typeof v.url === "string" && v.url.trim()) return v.url.trim();
  }
  for (const v of Object.values(ju)) {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (v && typeof v === "object" && typeof v.url === "string" && v.url.trim()) return v.url.trim();
  }
  return "";
}

/** 任务「去完成」按钮：优先展示后端 buttonText */
function taskDoButtonLabel(task, fallbackLabel) {
  const text = task?.buttonText;
  if (text != null && String(text).trim() !== "") return String(text).trim();
  return fallbackLabel;
}

/** 条件上的子步骤（接口可能挂在 steps / taskSteps / stepList） */
function getConditionSteps(task) {
  if (!task) return [];
  const raw = task.steps ?? task.taskSteps ?? task.stepList;
  return Array.isArray(raw) ? raw : [];
}

function getStepLabel(step) {
  if (!step) return "";
  const s =
    step.stepName ??
    step.title ??
    step.name ??
    step.conditionDescription ??
    step.conditionName ??
    step.stepDescription ??
    step.description;
  return s != null && String(s).trim() !== "" ? String(s).trim() : "";
}

/** 已领取的阶梯：Ongoing 卡片内不再展示 */
function isOngoingRewardClaimed(reward) {
  const st = (reward.status || "").toUpperCase();
  return st === "CLAIMED" || st === "SENT" || st === "COMPLETED";
}

/**
 * Ongoing 卡片只展示「当前」一条 reward：按 rewards 顺序跳过已领取，取第一条未领取的；
 * 其后梯度一律不展示（任务进行中 / 待领取均属于当前条）。
 */
function pickOngoingCurrentReward(rewards) {
  if (!Array.isArray(rewards)) return null;
  for (let i = 0; i < rewards.length; i++) {
    const r = rewards[i];
    if (!isOngoingRewardClaimed(r)) return r;
  }
  return null;
}

/** 手风琴标题：优先 rewardTier（梯度） */
function ongoingRewardTierTitle(reward, visibleIndex, t) {
  const tier = reward.rewardTier;
  if (tier != null && String(tier).trim() !== "") {
    return t("activity.rewardTierLabel", { tier: String(tier).trim() }) || `Tier ${tier}`;
  }
  return t("activity.rewardN", { n: visibleIndex + 1 }) || `Reward ${visibleIndex + 1}`;
}

/** Past / Ongoing 卡片展示奖励数量时的默认货币（rewardCurrency 为空时） */
const DEFAULT_REWARD_CURRENCY = "USDT";

function shouldAggregatePastActivity(activity) {
  const rewards = activity?.rewards || [];
  if (rewards.length <= 1) return false;
  return rewards.some((r) => (r.conditions || []).length > 1);
}

/** 单条 reward：conditions 的 progress 汇总 + reward 层货币（默认 USDT） */
/** Ongoing：未参加的活动不展示（与 Join 列表 NOT_JOINED 一致） */
function isOngoingActivityParticipating(activity) {
  const s = (activity?.userStatus || "").toUpperCase();
  return s !== "NOT_JOINED";
}

/** Join：常驻活动不展示 End in（permanent 为 true / 1 / 常驻 等） */
function isJoinActivityPermanent(activity) {
  const p = activity?.permanent;
  if (p === true || p === 1) return true;
  if (typeof p === "string") {
    const s = p.trim();
    if (!s) return false;
    if (/^(1|true|yes|permanent|常驻)$/i.test(s)) return true;
  }
  return false;
}

/**
 * Join 列表：endTime 有有效值且已早于当前时间则视为已结束（不展示）；
 * endTime 为 null / 空串 / 无法解析为有效时间则照常展示。
 */
function isJoinActivityStillOpenByEndTime(activity, referenceMs = Date.now()) {
  const raw = activity?.endTime;
  if (raw == null || raw === "") return true;
  let ts = typeof raw === "string" ? Number(raw) : raw;
  if (!Number.isFinite(ts)) return true;
  if (ts > 0 && ts < 1e12) ts *= 1000;
  return ts >= referenceMs;
}

function sumRewardConditionsProgress(reward) {
  const conds = reward?.conditions || [];
  let sumCur = 0;
  let sumTgt = 0;
  conds.forEach((c) => {
    sumCur += Number(c.progressCurrent) || 0;
    sumTgt += Number(c.progressTarget) || 0;
  });
  const cur = reward?.rewardCurrency;
  const unit =
    cur != null && String(cur).trim() !== "" ? String(cur).trim() : DEFAULT_REWARD_CURRENCY;
  return { sumCur, sumTgt, unit };
}

/** 单条 reward + 所属 activity → 与 flatten 卡片相同字段，供 Ongoing 内嵌渲染 */
function activityRewardToCard(activity, reward) {
  const tasks = reward.conditions || [];
  return {
    activityId: activity.activityId,
    activityRewardId: reward.activityRewardId,
    activityName: activity.activityName,
    endTime: activity.endTime,
    timeRangeText: activity.timeRangeText,
    userStatus: activity.userStatus,
    cardType: tasks.length > 1 ? "MULTI_TASK" : "SINGLE_TASK",
    tasks,
    status: reward.status,
    statusText: reward.statusText,
    userRewardRecordId: reward.userRewardRecordId,
    rewardAmount: reward.rewardAmount,
    rewardCurrency: reward.rewardCurrency,
    rewardDescription: reward.rewardDescription,
    rewardTier: reward.rewardTier,
    expiredAt: reward.expiredAt,
  };
}

function parseTimestampToMs(ts) {
  if (ts == null || ts === "") return null;
  let n = typeof ts === "string" ? Number(ts) : ts;
  if (!Number.isFinite(n)) return null;
  if (n > 0 && n < 1e12) n *= 1000;
  return n;
}

/** 接口可能返回 NOT JOINED，统一成 NOT_JOINED */
function normalizeActivityUserStatus(raw) {
  return String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

/** Past 单卡：该档 conditions 是否全部完成 */
function rewardConditionsAllCompleteForCard(card) {
  const tasks = card.tasks || [];
  if (!tasks.length) return false;
  return tasks.every(isTaskCompleted);
}

/** 已超过奖励领取截止时间（expiredAt 为空表示不设限，不算过期） */
function isRewardClaimDeadlinePassed(expiredAt, nowMs) {
  const ms = parseTimestampToMs(expiredAt);
  if (ms == null) return false;
  return nowMs >= ms;
}

function pastCompleteTaskTimeValue(card, t, nowMs, formatEndTimeToDHMFn) {
  const et = card.endTime;
  const missing =
    et == null || et === "" || (typeof et === "number" && !Number.isFinite(et));
  if (!missing) {
    const v = formatEndTimeToDHMFn(et);
    return v || (t("activity.timePlaceholder") || "—");
  }
  const tr = card.timeRangeText;
  if (tr != null && String(tr).trim() !== "") return String(tr).trim();
  return t("activity.timePlaceholder") || "—";
}

function pastClaimTimeValue(card, t, nowMs, formatEndTimeToDHMFn) {
  const ms = parseTimestampToMs(card.expiredAt);
  if (ms == null) {
    const tr = card.timeRangeText;
    if (tr != null && String(tr).trim() !== "") return String(tr).trim();
    return t("activity.timePlaceholder") || "—";
  }
  if (nowMs >= ms) return t("activity.claimExpired") || t("activity.ended") || "Ended";
  return formatEndTimeToDHMFn(card.expiredAt) || (t("activity.timePlaceholder") || "—");
}

function pastCardTimeRow(card, t, nowMs, formatEndTimeToDHMFn) {
  const allDone = rewardConditionsAllCompleteForCard(card);
  if (!allDone) {
    return {
      label: t("activity.timeLeft") || "Time Left to Complete Task",
      value: pastCompleteTaskTimeValue(card, t, nowMs, formatEndTimeToDHMFn),
    };
  }
  return {
    label: t("activity.timeLeftToClaim") || "Time Left to Claim Reward",
    value: pastClaimTimeValue(card, t, nowMs, formatEndTimeToDHMFn),
  };
}

/** 活动下每条 reward 的 conditions 是否都完成 */
function activityRewardsAllConditionsComplete(activity) {
  const rewards = activity?.rewards || [];
  if (!rewards.length) return false;
  return rewards.every((r) => {
    const conds = r.conditions || [];
    if (!conds.length) return false;
    return conds.every(isTaskCompleted);
  });
}

function isRewardClaimedLike(r) {
  const x = (r?.status || "").toString().toUpperCase();
  return x === "CLAIMED" || x === "SENT" || x === "COMPLETED";
}

function pastAggregatedCompleteTaskTimeValue(activity, t, nowMs, formatEndTimeToDHMFn) {
  const et = activity?.endTime;
  const missing =
    et == null || et === "" || (typeof et === "number" && !Number.isFinite(et));
  if (!missing) {
    const v = formatEndTimeToDHMFn(et);
    return v || (t("activity.timePlaceholder") || "—");
  }
  const tr = activity?.timeRangeText;
  if (tr != null && String(tr).trim() !== "") return String(tr).trim();
  return t("activity.timePlaceholder") || "—";
}

/** 多档聚合：取未领取档中最早的领取截止时间用于倒计时 */
function pastAggregatedClaimTimeValue(activity, t, nowMs, formatEndTimeToDHMFn) {
  const rewards = activity?.rewards || [];
  const candidates = rewards.filter((r) => !isRewardClaimedLike(r));
  const deadlines = candidates
    .map((r) => parseTimestampToMs(r.expiredAt))
    .filter((ms) => ms != null);
  if (!deadlines.length) {
    const tr = activity?.timeRangeText;
    if (tr != null && String(tr).trim() !== "") return String(tr).trim();
    return t("activity.timePlaceholder") || "—";
  }
  const minMs = Math.min(...deadlines);
  if (nowMs >= minMs) return t("activity.claimExpired") || t("activity.ended") || "Ended";
  return formatEndTimeToDHMFn(minMs) || (t("activity.timePlaceholder") || "—");
}

function pastAggregatedTimeRow(activity, t, nowMs, formatEndTimeToDHMFn) {
  const allDone = activityRewardsAllConditionsComplete(activity);
  if (!allDone) {
    return {
      label: t("activity.timeLeft") || "Time Left to Complete Task",
      value: pastAggregatedCompleteTaskTimeValue(activity, t, nowMs, formatEndTimeToDHMFn),
    };
  }
  return {
    label: t("activity.timeLeftToClaim") || "Time Left to Claim Reward",
    value: pastAggregatedClaimTimeValue(activity, t, nowMs, formatEndTimeToDHMFn),
  };
}

/**
 * Past 领取按钮：活动 userStatus（FAILED/EXPIRED）优先；
 * 再判断任务是否完成、是否在 rewards.expiredAt 领取窗口内、rewards.status 与 userRewardRecordId。
 */
function getPastRewardButtonState(card, t, nowMs) {
  const actSt = normalizeActivityUserStatus(card.userStatus);
  if (actSt === "FAILED") {
    return {
      canClaim: false,
      label: t("activity.challengeFailed") || "Challenge failed",
      disabled: true,
    };
  }
  if (actSt === "EXPIRED") {
    return {
      canClaim: false,
      label: t("activity.activityExpired") || "Expired",
      disabled: true,
    };
  }

  const rStatus = (card.status || "").toString().toUpperCase();
  const allDone = rewardConditionsAllCompleteForCard(card);

  if (rStatus === "CLAIMED" || rStatus === "SENT") {
    return { canClaim: false, label: t("activity.claimed") || "Claimed", disabled: true };
  }
  if (rStatus === "COMPLETED") {
    return { canClaim: false, label: t("activity.completed") || "Completed", disabled: true };
  }

  if (allDone && isRewardClaimDeadlinePassed(card.expiredAt, nowMs)) {
    return {
      canClaim: false,
      label: t("activity.claimExpired") || "Claim expired",
      disabled: true,
    };
  }

  const canClaim =
    allDone &&
    !isRewardClaimDeadlinePassed(card.expiredAt, nowMs) &&
    rStatus === "AVAILABLE" &&
    !!card.userRewardRecordId;

  if (canClaim) {
    return { canClaim: true, label: t("activity.claimReward") || "Claim Reward", disabled: false };
  }

  return {
    canClaim: false,
    label: pastRewardFooterLabel(card, t),
    disabled: true,
  };
}

function findPastClaimableReward(activity, t, nowMs) {
  const rewards = activity?.rewards || [];
  for (let i = 0; i < rewards.length; i++) {
    const card = activityRewardToCard(activity, rewards[i]);
    if (getPastRewardButtonState(card, t, nowMs).canClaim) return rewards[i];
  }
  return null;
}

/**
 * Past 卡片底部：与接口 result.rewards[].status 对齐
 * PENDING / AVAILABLE / LOCKED / CLAIMED；无记录为 null。
 */
function pastRewardFooterLabel(reward, t) {
  const raw = reward?.status;
  if (raw == null || raw === "") {
    const st = reward?.statusText && String(reward.statusText).trim();
    return st || t("activity.rewardNoRecord") || "—";
  }
  const s = String(raw).toUpperCase();
  if (s === "CLAIMED") {
    return t("activity.claimed") || "Claimed";
  }
  if (s === "PENDING") {
    return t("activity.rewardPending") || "Pending";
  }
  if (s === "LOCKED") {
    return t("activity.rewardLocked") || "Locked";
  }
  if (s === "AVAILABLE") {
    return reward?.userRewardRecordId
      ? t("activity.claimReward") || "Claim Reward"
      : t("activity.rewardUnavailable") || "Unavailable";
  }
  if (s === "FAILED" || s === "CHALLENGE_FAILED") {
    return t("activity.challengeFailed") || "Challenge failed";
  }
  if (s === "EXPIRED") {
    return t("activity.expired") || "Expired";
  }
  if (s === "COMPLETED" || s === "SENT") {
    return t("activity.completed") || "Completed";
  }
  const st = reward?.statusText && String(reward.statusText).trim();
  if (st) return st;
  return t("activity.ended") || "Ended";
}

/** 聚合卡底部文案：活动级失败/过期优先，其次领取超时，再按档位 status */
function aggregatePastActivityFooterLabel(activity, t, nowMs) {
  const actSt = normalizeActivityUserStatus(activity?.userStatus);
  if (actSt === "FAILED") {
    return t("activity.challengeFailed") || "Challenge failed";
  }
  if (actSt === "EXPIRED") {
    return t("activity.activityExpired") || "Expired";
  }

  const rewards = activity?.rewards || [];
  if (!rewards.length) return t("activity.ended") || "Ended";
  if (rewards.every(isRewardClaimedLike)) {
    return t("activity.claimed") || "Claimed";
  }

  for (let i = 0; i < rewards.length; i++) {
    const r = rewards[i];
    if (isRewardClaimedLike(r)) continue;
    const card = activityRewardToCard(activity, r);
    if (
      rewardConditionsAllCompleteForCard(card) &&
      isRewardClaimDeadlinePassed(card.expiredAt, nowMs)
    ) {
      return t("activity.claimExpired") || "Claim expired";
    }
  }

  const notable = rewards.find((r) => !isRewardClaimedLike(r));
  return pastRewardFooterLabel(notable || rewards[0], t);
}

const PointsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" className={styles.summaryLabelIcon}>
    <path d="M1.31396 3.32917C1.31396 3.64541 1.43517 3.95856 1.67067 4.25073C1.90616 4.5429 2.25133 4.80837 2.68647 5.03199C3.1216 5.2556 3.63819 5.43299 4.20672 5.55401C4.77525 5.67503 5.3846 5.73732 5.99998 5.73732C6.61536 5.73732 7.22471 5.67503 7.79324 5.55401C8.36177 5.43299 8.87836 5.2556 9.31349 5.03199C9.74863 4.80837 10.0938 4.5429 10.3293 4.25073C10.5648 3.95856 10.686 3.64541 10.686 3.32917C10.686 2.69049 10.1923 2.07797 9.31349 1.62635C8.43469 1.17474 7.24279 0.921021 5.99998 0.921021C4.75717 0.921021 3.56527 1.17474 2.68647 1.62635C1.80767 2.07797 1.31396 2.69049 1.31396 3.32917Z" fill="white" fillOpacity="0.56" />
    <path d="M5.99998 6.46184C4.0859 6.46184 2.43992 5.87207 1.71244 5.02686C1.45631 5.32445 1.31396 5.65368 1.31396 6.00002C1.31396 7.33 3.41197 8.40817 5.99998 8.40817C8.58799 8.40817 10.686 7.33 10.686 6.00002C10.686 5.65368 10.5437 5.32445 10.2875 5.02686C9.56004 5.87207 7.91406 6.46184 5.99998 6.46184Z" fill="white" fillOpacity="0.56" />
    <path d="M5.99998 9.19902C4.06092 9.19902 2.39697 8.59377 1.68439 7.73083C1.44591 8.01964 1.31396 8.3373 1.31396 8.67079C1.31396 10.0008 3.41197 11.0789 5.99998 11.0789C8.58799 11.0789 10.686 10.0008 10.686 8.67079C10.686 8.33729 10.554 8.01964 10.3156 7.73083C9.60299 8.59377 7.93904 9.19902 5.99998 9.19902Z" fill="white" fillOpacity="0.56" />
  </svg>
);

const VouchersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" className={styles.summaryLabelIcon}>
    <path d="M10.7356 4.60427C11.0474 4.48826 11.2571 4.19412 11.2571 3.86248V2.24646C11.2571 1.80583 10.9009 1.44958 10.4603 1.44958H1.54932C1.10869 1.44958 0.752441 1.80583 0.752441 2.24646V3.88005C0.752441 4.21169 0.961035 4.50466 1.27158 4.62185C1.83643 4.83513 2.23838 5.38123 2.23838 6.02107C2.23838 6.66091 1.83643 7.20701 1.27158 7.42029C0.961035 7.53748 0.752441 7.83044 0.752441 8.16208V9.69958C0.752441 10.1402 1.10869 10.4965 1.54932 10.4965H10.4591C10.8997 10.4965 11.256 10.1402 11.256 9.69958V8.15037C11.256 7.81755 11.0462 7.52458 10.7345 7.40857C10.1661 7.19529 9.76182 6.64802 9.76182 6.00583C9.76182 5.36365 10.1661 4.81638 10.7356 4.60427ZM8.23838 7.12498C8.23838 7.33357 8.06963 7.50232 7.86104 7.50232H4.12393C3.91533 7.50232 3.74658 7.33357 3.74658 7.12498C3.74658 6.91638 3.91533 6.74763 4.12393 6.74763H7.86104C8.06846 6.74763 8.23838 6.91638 8.23838 7.12498ZM8.24189 4.87498C8.24189 5.08357 8.07314 5.25232 7.86455 5.25232H4.12861C3.92002 5.25232 3.75127 5.08357 3.75127 4.87498C3.75127 4.66638 3.92002 4.49763 4.12861 4.49763H7.86572C8.07314 4.49763 8.24189 4.66638 8.24189 4.87498Z" fill="white" fillOpacity="0.56" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none" className={styles.arrowRightIcon}>
    <path d="M3.56394 4.58208L-6.19888e-05 8.14608L1.01802 9.16416L5.6001 4.58208L1.01802 0L-6.19888e-05 1.01808L3.56394 4.58208Z" fill="#B9F78A" />
  </svg>
);

const RuleArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="5" height="7" viewBox="0 0 5 7" fill="none" className={styles.ruleArrowIcon}>
    <path d="M2.67308 3.43656L7.53403e-05 6.10956L0.763635 6.87312L4.2002 3.43656L0.763635 0L7.53403e-05 0.76356L2.67308 3.43656Z" fill="white" />
  </svg>
);

const CheckInDescArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none" className={styles.checkInDescArrowIcon}>
    <path d="M3.56394 4.58208L-6.19888e-05 8.14608L1.01802 9.16416L5.6001 4.58208L1.01802 0L-6.19888e-05 1.01808L3.56394 4.58208Z" fill="#707A8A" />
  </svg>
);

const TaskInfoIcon = () => (
  <span className={styles.taskInfoIcon} aria-hidden>
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M7 12.25C9.8995 12.25 12.25 9.8995 12.25 7C12.25 4.1005 9.8995 1.75 7 1.75C4.1005 1.75 1.75 4.1005 1.75 7C1.75 9.8995 4.1005 12.25 7 12.25ZM6.27083 4.95833V3.5H7.72917V4.95833H6.27083ZM6.27083 10.5V6.41667H7.72917V10.5H6.27083Z" fill="#707A8A" />
    </svg>
  </span>
);

const TaskCardIcon = ({ gradientId = "taskcard" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.taskCardIconSvg}>
    <path fillRule="evenodd" clipRule="evenodd" d="M7 13C4.79086 13 3 14.7909 3 17V21H21V17C21 14.7909 19.2091 13 17 13H15L12 16L9 13L7 13Z" fill="#76808F" />
    <path fillRule="evenodd" clipRule="evenodd" d="M16.5 6.5C16.5 4.01472 14.4853 2 12 2C9.51472 2 7.5 4.01472 7.5 6.5C7.5 8.98528 9.51472 11 12 11C14.4853 11 16.5 8.98528 16.5 6.5Z" fill={`url(#paint0_linear_${gradientId})`} />
    <defs>
      <linearGradient id={`paint0_linear_${gradientId}`} x1="12" y1="11" x2="12" y2="2" gradientUnits="userSpaceOnUse">
        <stop stopColor="#B9F78A" />
        <stop offset="1" stopColor="#9BD96C" />
      </linearGradient>
    </defs>
  </svg>
);

const JoinActivityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none" className={styles.joinActivityCardIconSvg}>
    <rect width="64" height="64" rx="4" fill="#32343B" />
    <path
      d="M47.556 40.0921L41.9611 30.3357C41.6763 29.8463 41.2681 29.4402 40.7772 29.1579C40.2863 28.8757 39.73 28.7271 39.1637 28.7271C38.5975 28.7271 38.0411 28.8757 37.5502 29.1579C37.0593 29.4402 36.6511 29.8463 36.3663 30.3357L34.1468 34.1735L28.8756 25.0183C28.5908 24.5289 28.1826 24.1228 27.6917 23.8405C27.2008 23.5582 26.6445 23.4097 26.0782 23.4097C25.512 23.4097 24.9556 23.5582 24.4647 23.8405C23.9738 24.1228 23.5656 24.5289 23.2808 25.0183L14.5879 40.0921C14.3016 40.5785 14.1495 41.1321 14.147 41.6966C14.1445 42.261 14.2918 42.8159 14.5739 43.3048C14.856 43.7937 15.2627 44.1991 15.7525 44.4794C16.2424 44.7598 16.7978 44.9052 17.3623 44.9009H44.7817C45.3461 44.9052 45.9016 44.7598 46.3914 44.4794C46.8813 44.1991 47.288 43.7937 47.57 43.3048C47.8521 42.8159 47.9994 42.261 47.9969 41.6966C47.9945 41.1321 47.8423 40.5785 47.556 40.0921Z"
      fill="#23252A"
    />
    <path
      d="M35.9038 21.5966C35.9038 22.1431 36.0114 22.6843 36.2206 23.1892C36.4297 23.6941 36.7362 24.1528 37.1227 24.5392C37.5091 24.9257 37.9679 25.2322 38.4728 25.4413C38.9776 25.6505 39.5188 25.7581 40.0653 25.7581C40.6118 25.7581 41.1529 25.6505 41.6578 25.4413C42.1627 25.2322 42.6214 24.9257 43.0079 24.5392C43.3943 24.1528 43.7008 23.6941 43.91 23.1892C44.1191 22.6843 44.2267 22.1431 44.2267 21.5966C44.2267 21.0502 44.1191 20.509 43.91 20.0041C43.7008 19.4992 43.3943 19.0405 43.0079 18.654C42.6214 18.2676 42.1627 17.9611 41.6578 17.752C41.1529 17.5428 40.6118 17.4352 40.0653 17.4352C39.5188 17.4352 38.9776 17.5428 38.4728 17.752C37.9679 17.9611 37.5091 18.2676 37.1227 18.654C36.7362 19.0405 36.4297 19.4992 36.2206 20.0041C36.0114 20.509 35.9038 21.0502 35.9038 21.5966Z"
      fill="#23252A"
    />
  </svg>
);
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M6 2.66665C6.00001 2.57909 5.98278 2.49239 5.94928 2.41149C5.91579 2.3306 5.86668 2.25709 5.80477 2.19518C5.74287 2.13326 5.66937 2.08414 5.58848 2.05063C5.50759 2.01713 5.42089 1.99988 5.33333 1.99988C5.24578 1.99988 5.15908 2.01713 5.07819 2.05063C4.9973 2.08414 4.9238 2.13326 4.86189 2.19518C4.79999 2.25709 4.75088 2.3306 4.71738 2.41149C4.68389 2.49239 4.66665 2.57909 4.66667 2.66665V3.33331C4.36067 3.33331 4.09333 3.33531 3.87 3.35331C3.60667 3.37531 3.344 3.42265 3.092 3.55131C2.71569 3.74306 2.40974 4.04901 2.218 4.42531C2.08933 4.67731 2.042 4.93998 2.02067 5.20331C2.00933 5.34131 2.004 5.49665 2.002 5.66665C1.99933 5.85065 2.14933 5.99998 2.33333 5.99998H13.6667C13.7104 6.00007 13.7537 5.99149 13.794 5.97475C13.8344 5.95801 13.8711 5.93342 13.9019 5.90243C13.9327 5.87143 13.957 5.83463 13.9735 5.79416C13.99 5.75369 13.9984 5.71035 13.998 5.66665C13.996 5.49598 13.9913 5.34131 13.9793 5.20331C13.958 4.93998 13.9107 4.67731 13.782 4.42531C13.5903 4.04901 13.2843 3.74306 12.908 3.55131C12.656 3.42265 12.3933 3.37531 12.13 3.35398C11.906 3.33531 11.6393 3.33331 11.3333 3.33331V2.66665C11.3333 2.57909 11.3161 2.49239 11.2826 2.41149C11.2491 2.3306 11.2 2.25709 11.1381 2.19518C11.0762 2.13326 11.0027 2.08414 10.9218 2.05063C10.8409 2.01713 10.7542 1.99988 10.6667 1.99988C10.5791 1.99988 10.4924 2.01713 10.4115 2.05063C10.3306 2.08414 10.2571 2.13326 10.1952 2.19518C10.1333 2.25709 10.0842 2.3306 10.0507 2.41149C10.0172 2.49239 9.99999 2.57909 10 2.66665V3.33331H6V2.66665ZM14 7.66665C14 7.62287 13.9914 7.57953 13.9746 7.53909C13.9579 7.49864 13.9333 7.4619 13.9024 7.43094C13.8714 7.39999 13.8347 7.37544 13.7942 7.35869C13.7538 7.34194 13.7104 7.33331 13.6667 7.33331H2.33333C2.28956 7.33331 2.24621 7.34194 2.20577 7.35869C2.16533 7.37544 2.12858 7.39999 2.09763 7.43094C2.06668 7.4619 2.04213 7.49864 2.02537 7.53909C2.00862 7.57953 2 7.62287 2 7.66665V11.226C2 11.5773 2 11.8806 2.02 12.13C2.042 12.3933 2.08933 12.656 2.218 12.908C2.40974 13.2843 2.71569 13.5902 3.092 13.782C3.344 13.9106 3.60667 13.958 3.87 13.9793C4.12 14 4.42333 14 4.774 14H11.226C11.5773 14 11.8807 14 12.13 13.98C12.3933 13.958 12.656 13.9106 12.908 13.782C13.2843 13.5902 13.5903 13.2843 13.782 12.908C13.9107 12.656 13.958 12.3933 13.9793 12.13C14 11.88 14 11.5766 14 11.226V7.66665Z"
      fill="#707A8A"
    />
  </svg>
);

export default function RewardHubContent() {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const loginStatus = appState?.loginStatus;
  const userInfo = appState?.userInfo ?? null;
  const [primaryTab, setPrimaryTab] = useState("completeTasks");
  const [secondaryTab, setSecondaryTab] = useState("ongoing");
  const [ruleDrawerVisible, setRuleDrawerVisible] = useState(false);

  const [ongoingActivities, setOngoingActivities] = useState([]);
  const [pastActivities, setPastActivities] = useState([]);
  /** 接口返回的 NOT_JOINED 全量；展示时再按 endTime + 当前时间过滤，到点自动消失 */
  const [joinActivitiesRaw, setJoinActivitiesRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [claimingIds, setClaimingIds] = useState({});
  const [joiningIds, setJoiningIds] = useState({});
  const [now, setNow] = useState(Date.now());
  const ongoingTabVisitCount = useRef(0);
  const [voucherTotal, setVoucherTotal] = useState(0);
  const [voucherCountLoading, setVoucherCountLoading] = useState(false);

  const couponTenantId = useMemo(() => resolveCouponTenantId(appState), [appState]);

  const loadVoucherCount = useCallback(async () => {
    if (!loginStatus || userInfo?.userId == null || userInfo.userId === "") {
      setVoucherTotal(0);
      setVoucherCountLoading(false);
      return;
    }
    setVoucherCountLoading(true);
    try {
      const { ok, totalCount } = await fetchUserCouponTotalCount({
        tenantId: couponTenantId,
        userId: String(userInfo.userId),
      });
      setVoucherTotal(ok && totalCount != null ? totalCount : 0);
    } catch (e) {
      console.error("[RewardHub] voucher count", e);
      setVoucherTotal(0);
    } finally {
      setVoucherCountLoading(false);
    }
  }, [loginStatus, userInfo?.userId, couponTenantId]);

  useEffect(() => {
    loadVoucherCount();
  }, [loadVoucherCount]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchOngoingCards = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoading(true);
    try {
      const list = await getActivityList({
        params: { ongoingOnly: true },
      });
      const arr = Array.isArray(list) ? list : [];
      setOngoingActivities(arr.filter(isOngoingActivityParticipating));
    } catch (e) {
      console.error("fetchOngoingCards", e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchJoinActivities = useCallback(async () => {
    try {
      const list = await getActivityList();
      const arr = Array.isArray(list) ? list : [];
      setJoinActivitiesRaw(arr.filter((a) => a.userStatus === "NOT_JOINED"));
    } catch (e) {
      console.error("fetchJoinActivities", e);
    }
  }, []);

  const joinActivitiesVisible = useMemo(
    () => joinActivitiesRaw.filter((a) => isJoinActivityStillOpenByEndTime(a, now)),
    [joinActivitiesRaw, now]
  );

  const fetchPastData = useCallback(async () => {
    try {
      const list = await getActivityListPast({
        params: { participationOutcome: "ALL" },
      });
      setPastActivities(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("fetchPastData", e);
      setPastActivities([]);
    }
  }, []);

  /** 按当前 Tab 拉数：Ongoing 用 list?ongoingOnly=true；Join 用完整 list；Past 用 list/past */
  useEffect(() => {
    if (primaryTab === "completeTasks" && secondaryTab === "ongoing") {
      ongoingTabVisitCount.current += 1;
      fetchOngoingCards({ silent: ongoingTabVisitCount.current > 1 });
    } else if (primaryTab === "completeTasks" && secondaryTab === "past") {
      fetchPastData();
    } else if (primaryTab === "joinActivities") {
      fetchJoinActivities();
    }
  }, [primaryTab, secondaryTab, fetchOngoingCards, fetchPastData, fetchJoinActivities]);

  const handleClaimReward = async (userRewardRecordId) => {
    if (!userRewardRecordId || claimingIds[userRewardRecordId]) return;
    setClaimingIds((prev) => ({ ...prev, [userRewardRecordId]: true }));
    try {
      await claimActivityReward(userRewardRecordId);
      message.success(t("activity.claimSuccess") || "Reward claimed!");
      await Promise.all([fetchOngoingCards({ silent: true }), fetchPastData()]);
    } catch (e) {
      console.error("claimReward", e);
    }
    setClaimingIds((prev) => ({ ...prev, [userRewardRecordId]: false }));
  };

  const handleJoinActivity = async (activityId) => {
    if (!activityId || joiningIds[activityId]) return;
    setJoiningIds((prev) => ({ ...prev, [activityId]: true }));
    try {
      await joinActivity(activityId);
      message.success(t("activity.joinSuccess") || "Joined successfully!");
      await Promise.all([fetchOngoingCards({ silent: true }), fetchJoinActivities()]);
    } catch (e) {
      console.error("joinActivity", e);
    }
    setJoiningIds((prev) => ({ ...prev, [activityId]: false }));
  };

  const openActivityDetail = useCallback((activityId) => {
    if (!activityId) return;
    window.location.href = getUrl(`/activity/${activityId}`);
  }, []);

  const renderActivityTitle = (title, activityId, className) => (
    <div className={styles.titleRow}>
      <h3 className={className}>{title || ""}</h3>
      {activityId ? (
        <button
          type="button"
          className={styles.titleDetailBtn}
          onClick={() => openActivityDetail(activityId)}
          aria-label={t("pizzaday.view") || "View"}
        >
          <ArrowRightIcon />
        </button>
      ) : null}
    </div>
  );

  /** 将截止毫秒时间戳转为倒计时展示；单位文案走 activity.timeSuffix* */
  const formatEndTimeToDHM = (rawEndTime, referenceMs = now) => {
    if (rawEndTime == null || rawEndTime === "") return "";
    let ts = typeof rawEndTime === "string" ? Number(rawEndTime) : rawEndTime;
    if (!Number.isFinite(ts)) return "";
    if (ts > 0 && ts < 1e12) ts *= 1000;
    const diff = ts - referenceMs;
    const clamped = diff > 0 ? diff : 0;
    const d = Math.floor(clamped / 86400000);
    const h = Math.floor((clamped % 86400000) / 3600000);
    const m = Math.floor((clamped % 3600000) / 60000);
    const dU = t("activity.timeSuffixD") || "D";
    const hU = t("activity.timeSuffixH") || "H";
    const mU = t("activity.timeSuffixM") || "M";
    return `${d}${dU} : ${String(h).padStart(2, "0")}${hU} : ${String(m).padStart(2, "0")}${mU}`;
  };

  const formatTimeLeft = (endTime) => {
    if (!endTime) return "";
    const diff = endTime - now;
    if (diff <= 0) return t("activity.ended") || "Ended";
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const dU = t("activity.timeSuffixD") || "D";
    const hU = t("activity.timeSuffixH") || "H";
    const mU = t("activity.timeSuffixM") || "M";
    return `${d}${dU} : ${String(h).padStart(2, "0")}${hU} : ${String(m).padStart(2, "0")}${mU}`;
  };

  const formatTimeLeftLong = (endTime) => {
    if (!endTime) return "";
    const diff = endTime - now;
    if (diff <= 0) return t("activity.ended") || "Ended";
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    const dU = t("activity.timeSuffixD") || "D";
    const hU = t("activity.timeSuffixH") || "H";
    const mU = t("activity.timeSuffixM") || "M";
    const sU = t("activity.timeSuffixS") || "S";
    return `${d}${dU} : ${String(h).padStart(2, "0")}${hU} : ${String(m).padStart(2, "0")}${mU} : ${String(s).padStart(2, "0")}${sU}`;
  };

  /** endTime 无值时用后端 timeRangeText（活动级） */
  const getActivityTimeRemainingDisplay = (activity, long = false) => {
    const et = activity?.endTime;
    const missing =
      et == null ||
      et === "" ||
      (typeof et === "number" && !Number.isFinite(et));
    if (!missing) {
      return long ? formatTimeLeftLong(et) : formatTimeLeft(et);
    }
    const tr = activity?.timeRangeText;
    return tr != null && String(tr).trim() !== "" ? String(tr).trim() : "";
  };

  const openTaskJumpUrl = useCallback(
    async (task) => {
      const depositAmountRule = getDepositAmountRuleFromCondition(task);

      if (shouldUseAppRouteForTaskJump()) {
        const nativeRaw = resolveTaskNativeJumpPath(task);
        if (nativeRaw) {
          let pathForApp = depositAmountRule
            ? appendActivityDepositQueryToPath(nativeRaw, { depositAmountRule })
            : nativeRaw;
          pathForApp = await resolveActivityWebJumpPath(
            pathForApp,
            userInfo,
            loginStatus
          );
          const appPath = normalizeAppRoutePath(pathForApp);
          if (appPath) {
            Bridge.call("appRoute", { path: appPath, params: "" });
            return;
          }
        }
      }

      const raw = resolveTaskJumpUrl(task);
      const url = (raw || "").trim();
      if (!url) {
        message.warning(t("activity.taskNoLink") || "No link available");
        return;
      }
      const withActivity = depositAmountRule
        ? appendActivityDepositQueryToPath(url, { depositAmountRule })
        : url;
      const finalPath = await resolveActivityWebJumpPath(
        withActivity,
        userInfo,
        loginStatus
      );
      if (/^https?:\/\//i.test(finalPath)) {
        window.location.href = finalPath;
        return;
      }
      window.location.href = getUrl(
        finalPath.startsWith("/") ? finalPath : `/${finalPath}`
      );
    },
    [t, userInfo, loginStatus]
  );

  const formatRewardText = (reward, defaultCurrency = DEFAULT_REWARD_CURRENCY) => {
    const amt = reward.rewardAmount;
    const missing =
      amt == null ||
      amt === "" ||
      (typeof amt === "string" && !String(amt).trim());
    if (!missing) {
      return `${amt} ${reward.rewardCurrency || defaultCurrency}`;
    }
    return `0 ${reward.rewardCurrency || defaultCurrency}`;
  };

  const renderConditionSteps = (tk, stepKeyPrefix) => {
    const steps = getConditionSteps(tk).filter((s) => getStepLabel(s));
    if (!steps.length) return null;
    return (
      <div className={styles.conditionSteps} role="list">
        <div className={styles.conditionStepsLabel}>{t("activity.taskSteps") || "Steps"}</div>
        {steps.map((st, si) => {
          const done = isTaskCompleted(st);
          const label = getStepLabel(st);
          return (
            <div
              key={`${stepKeyPrefix}-s-${st.stepId ?? st.id ?? si}`}
              className={styles.conditionStepRow}
              role="listitem"
            >
              <span className={styles.conditionStepIndex}>{si + 1}</span>
              <span className={done ? styles.conditionStepTextDone : styles.conditionStepText}>{label}</span>
              {done ? <span className={styles.conditionStepCheck}>✓</span> : null}
            </div>
          );
        })}
      </div>
    );
  };

  /** Ongoing 内嵌在活动卡片中的单条 reward（活动级标题与剩余时间由外层展示） */
  const renderOngoingRewardBlock = (card, keyPrefix, options = {}) => {
    const { activity: activityForTime, flatSingleTask = false } = options;
    const tasks = card.tasks || [];
    const isMultiTask = card.cardType === "MULTI_TASK";

    const tierHint =
      card.rewardTier != null && String(card.rewardTier).trim() !== ""
        ? ongoingRewardTierTitle(card, 0, t)
        : null;
    const descSmall =
      card.rewardDescription && String(card.rewardDescription).trim()
        ? String(card.rewardDescription).trim()
        : "";

    if (isMultiTask) {
      const completedCount = tasks.filter((tk) => isTaskCompleted(tk)).length;
      const totalCount = tasks.length;
      const pct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
      const allDone = totalCount > 0 && completedCount === totalCount;
      const canClaim = card.status === "AVAILABLE" && allDone;
      const isClaimed = card.status === "CLAIMED";

      if (canClaim && !isClaimed) {
        return (
          <article className={`${styles.taskGroup} ${styles.ongoingInnerTaskGroup}`}>
            {tierHint ? <p className={styles.ongoingRewardTierSub}>{tierHint}</p> : null}
            {descSmall ? <p className={styles.ongoingRewardDescriptionSmall}>{descSmall}</p> : null}
            <div className={styles.taskGroupSummary}>
              <div className={styles.taskItemRow}>
                <span className={styles.taskItemLabel}>{t("activity.reward") || "Reward"}</span>
                <span className={styles.taskItemValue}>
                  <img src={checkActiveIcon} alt="" className={styles.taskItemRewardIcon} />
                  {formatRewardText(card)}
                </span>
              </div>
            </div>
            <button
              type="button"
              className={styles.taskGroupClaimBtn}
              disabled={claimingIds[card.userRewardRecordId]}
              onClick={() => handleClaimReward(card.userRewardRecordId)}
            >
              {claimingIds[card.userRewardRecordId]
                ? t("activity.rewardClaimLoading") || "Loading"
                : t("activity.claimReward") || "Claim Reward"}
            </button>
          </article>
        );
      }

      const multiTitle = t("activity.completeNTasks", { n: totalCount }) || `Complete ${totalCount} Tasks`;

      return (
        <article className={`${styles.taskGroup} ${styles.ongoingInnerTaskGroup}`}>
          <h3 className={styles.taskGroupTitle}>{multiTitle}</h3>
          {tierHint ? <p className={styles.ongoingRewardTierSub}>{tierHint}</p> : null}
          {descSmall ? <p className={styles.ongoingRewardDescriptionSmall}>{descSmall}</p> : null}
          <div className={styles.taskGroupSummary}>
            <div className={styles.taskItemRow}>
              <span className={styles.taskItemLabel}>{t("activity.reward") || "Reward"}</span>
              <span className={styles.taskItemValue}>
                <img src={checkActiveIcon} alt="" className={styles.taskItemRewardIcon} />
                {formatRewardText(card)}
              </span>
            </div>
          </div>
          <div className={styles.taskGroupProgressWrap}>
            <div className={styles.taskGroupProgressBar}>
              <div className={styles.taskGroupProgressFill} style={{ width: `${pct}%` }} />
              <div className={styles.taskGroupProgressDot} style={{ left: `${pct}%` }} />
            </div>
            <span className={styles.taskGroupProgressText}>{completedCount}/{totalCount}</span>
          </div>
          <div className={styles.taskGroupList}>
            {tasks.map((tk, tIdx) => {
              const jumpUrl = resolveTaskJumpUrl(tk);
              const done = isTaskCompleted(tk);
              return (
                <div key={tk.activityConditionId || tIdx} className={styles.taskGroupTask}>
                  <div className={styles.taskGroupTaskHead}>
                    <div className={styles.taskItemIcon}><TaskCardIcon gradientId={`tg-${keyPrefix}-${tIdx}`} /></div>
                    <span className={`${styles.taskGroupTaskTitle} ${styles.taskConditionDescriptionText}`}>
                      {tIdx + 1}. {taskConditionDisplayText(tk, t)}
                    </span>
                  </div>
                  <div className={styles.taskGroupTaskRow}>
                    {shouldShowConditionProgressRow(tk) ? (
                      <>
                        <span className={styles.taskItemLabel}>{t("activity.progress") || "Progress"}</span>
                        <span className={styles.taskGroupTaskProgress}>
                          <span className={styles.taskItemValueGreen}>{tk.progressCurrent ?? 0}</span>
                          <span className={styles.taskItemValue}>/{tk.progressTarget ?? 0} {tk.progressCurrency || ""}</span>
                        </span>
                      </>
                    ) : null}
                    <div className={styles.taskGroupTaskActions}>
                      {/* <TaskInfoIcon /> */}
                      <button
                        type="button"
                        className={styles.taskItemBtn}
                        disabled={done}
                        onClick={() => {
                          if (done) return;
                          openTaskJumpUrl(tk);
                        }}
                      >
                        {done
                          ? (t("activity.completed") || "Completed")
                          : taskDoButtonLabel(tk, t("activity.doTask") || "Do Task")}
                      </button>
                    </div>
                  </div>
                  {renderConditionSteps(tk, `tg-${keyPrefix}-${tIdx}`)}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className={styles.taskGroupClaimBtn}
            disabled={!canClaim || isClaimed || claimingIds[card.userRewardRecordId]}
            onClick={() => canClaim && handleClaimReward(card.userRewardRecordId)}
          >
            {isClaimed
              ? (t("activity.claimed") || "Claimed")
              : claimingIds[card.userRewardRecordId]
                ? t("activity.rewardClaimLoading") || "Loading"
                : (t("activity.claimReward") || "Claim Reward")}
          </button>
        </article>
      );
    }

    const tk = tasks[0];
    const isClaimed = card.status === "CLAIMED";
    const taskCompleted = tk ? isTaskCompleted(tk) : false;
    const canClaimReward = card.status === "AVAILABLE" && taskCompleted;
    const jumpUrl = resolveTaskJumpUrl(tk);

    if (canClaimReward && !isClaimed) {
      const claimBody = (
        <>
          {tierHint ? <p className={styles.ongoingRewardTierSub}>{tierHint}</p> : null}
          {descSmall ? <p className={styles.ongoingRewardDescriptionSmall}>{descSmall}</p> : null}
          <div className={styles.taskItemRows}>
            <div className={styles.taskItemRow}>
              <span className={styles.taskItemLabel}>{t("activity.reward") || "Reward"}</span>
              <span className={styles.taskItemValue}>
                <img src={checkActiveIcon} alt="" className={styles.taskItemRewardIcon} />
                {formatRewardText(card)}
              </span>
            </div>
            {flatSingleTask && activityForTime ? (
              <div className={styles.taskItemRow}>
                <span className={styles.taskItemLabel}>{t("activity.timeLeft") || "Time Left to Complete Task"}</span>
                <span className={styles.taskItemValue}>{getActivityTimeRemainingDisplay(activityForTime, false)}</span>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className={styles.taskGroupClaimBtn}
            disabled={claimingIds[card.userRewardRecordId]}
            onClick={() => handleClaimReward(card.userRewardRecordId)}
          >
            {claimingIds[card.userRewardRecordId]
              ? t("activity.rewardClaimLoading") || "Loading"
              : t("activity.claimReward") || "Claim Reward"}
          </button>
        </>
      );
      if (flatSingleTask) {
        return <div className={styles.ongoingFlatSingleInner}>{claimBody}</div>;
      }
      return (
        <article className={`${styles.taskItem} ${styles.ongoingInnerTaskItem}`}>{claimBody}</article>
      );
    }

    let btnText;
    let btnDisabled;
    let btnAction;
    if (isClaimed) {
      btnText = t("activity.claimed") || "Claimed";
      btnDisabled = true;
    } else if (taskCompleted) {
      btnText = t("activity.claimReward") || "Claim Reward";
      btnDisabled = true;
    } else {
      btnText = taskDoButtonLabel(tk, t("activity.doTask") || "Do Task");
      btnDisabled = false;
      btnAction = "do";
    }

    const singleTitle =
      taskConditionDisplayText(tk, t) ||
      (card.rewardTier != null && String(card.rewardTier).trim() !== ""
        ? ongoingRewardTierTitle(card, 0, t)
        : t("activity.reward") || "Reward");

    const showProgressRow = tk && shouldShowConditionProgressRow(tk);

    const singleTaskBody = (
      <>
        <div className={styles.taskItemHead}>
          <div className={styles.taskItemIcon}><TaskCardIcon gradientId={`tc-${keyPrefix}`} /></div>
          <div className={styles.taskItemTitleBlock}>
            <h3 className={`${styles.taskItemTitle} ${styles.taskConditionDescriptionText}`}>{singleTitle}</h3>
            {descSmall ? (
              <p className={styles.ongoingRewardDescriptionSmall}>{descSmall}</p>
            ) : null}
          </div>
        </div>
        <div className={styles.taskItemRows}>
          {showProgressRow && (
            <div className={styles.taskItemRow}>
              <span className={styles.taskItemLabel}>{t("activity.progress") || "Progress"}</span>
              <span className={styles.taskItemValueGreen}>
                {tk.progressCurrent ?? 0}/{tk.progressTarget ?? 0} {tk.progressCurrency || ""}
              </span>
            </div>
          )}
          <div className={styles.taskItemRow}>
            <span className={styles.taskItemLabel}>{t("activity.reward") || "Reward"}</span>
            <span className={styles.taskItemValue}>
              <img src={checkActiveIcon} alt="" className={styles.taskItemRewardIcon} />
              {formatRewardText(card)}
            </span>
          </div>
          {flatSingleTask && activityForTime ? (
            <div className={styles.taskItemRow}>
              <span className={styles.taskItemLabel}>{t("activity.timeLeft") || "Time Left to Complete Task"}</span>
              <span className={styles.taskItemValue}>{getActivityTimeRemainingDisplay(activityForTime, false)}</span>
            </div>
          ) : null}
        </div>
        {tk ? renderConditionSteps(tk, `tc-${keyPrefix}`) : null}
        <div className={styles.taskItemFoot}>
          {/* <TaskInfoIcon /> */}
          <button
            type="button"
            className={styles.taskItemBtn}
            disabled={btnDisabled || claimingIds[card.userRewardRecordId]}
            onClick={() => {
              if (btnAction === "claim") handleClaimReward(card.userRewardRecordId);
              else if (btnAction === "do") openTaskJumpUrl(tk);
            }}
          >
            {btnText}
          </button>
        </div>
      </>
    );

    if (flatSingleTask) {
      return <div className={styles.ongoingFlatSingleInner}>{singleTaskBody}</div>;
    }

    return (
      <article className={`${styles.taskItem} ${styles.ongoingInnerTaskItem}`}>{singleTaskBody}</article>
    );
  };

  const renderOngoingTasks = () => {
    const activitiesWithCurrent = ongoingActivities
      .map((activity) => ({
        activity,
        currentReward: pickOngoingCurrentReward(activity.rewards || []),
      }))
      .filter(({ currentReward }) => currentReward != null);

    if (!activitiesWithCurrent.length) {
      return (
        <div className={styles.taskListEmpty}>
          <span className={styles.taskListEmptyText}>
            {t("activity.completeTasksEmpty") || "No tasks"}
          </span>
        </div>
      );
    }

    return activitiesWithCurrent.map(({ activity, currentReward }) => {
      const activityId = activity.activityId;
      const rewardKey = currentReward.activityRewardId ?? activityId;
      const rewards = activity.rewards || [];
      const flatSingleTask =
        rewards.length === 1 &&
        Array.isArray(currentReward.conditions) &&
        currentReward.conditions.length === 1;

      const block = renderOngoingRewardBlock(
        activityRewardToCard(activity, currentReward),
        `${activityId}-${rewardKey}`,
        { activity, flatSingleTask }
      );

      if (flatSingleTask) {
        return (
          <article key={`oa-${activityId}`} className={styles.taskItem}>
            {activity.activityName ? (
              renderActivityTitle(activity.activityName, activityId, styles.ongoingFlatActivityTitle)
            ) : null}
            {block}
          </article>
        );
      }

      return (
        <article key={`oa-${activityId}`} className={styles.taskGroup}>
          {renderActivityTitle(activity.activityName || "", activityId, styles.taskGroupTitle)}
          <div className={styles.taskGroupSummary}>
            <div className={styles.taskItemRow}>
              <span className={styles.taskItemLabel}>{t("activity.timeLeft") || "Time Left to Complete Task"}</span>
              <span className={styles.taskItemValue}>{getActivityTimeRemainingDisplay(activity, false)}</span>
            </div>
          </div>
          <div className={styles.ongoingCurrentRewardBody}>{block}</div>
        </article>
      );
    });
  };

  const renderPastRewardCard = (card, cardIdx) => {
    const tasks = card.tasks || [];
    const isMultiTask = card.cardType === "MULTI_TASK";
    const btnState = getPastRewardButtonState(card, t, now);
    const pastCurr = DEFAULT_REWARD_CURRENCY;
    const claiming = !!card.userRewardRecordId && !!claimingIds[card.userRewardRecordId];
    const timeRow = pastCardTimeRow(card, t, now, (et) => formatEndTimeToDHM(et));
    const canClaim = btnState.canClaim;
    const btnDisabled = btnState.disabled || claiming;
    const btnLabel = canClaim && claiming ? t("activity.rewardClaimLoading") || "Loading" : btnState.label;

    if (isMultiTask) {
      const completedCount = tasks.filter((tk) => isTaskCompleted(tk)).length;
      const totalCount = tasks.length;
      const pct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

      return (
        <article key={`pg-${card.activityId}-${card.activityRewardId}-${cardIdx}`} className={`${styles.taskGroup} ${styles.taskItemDisabled}`}>
          {renderActivityTitle(
            card.activityName || t("activity.completeNTasks", { n: totalCount }) || `Complete ${totalCount} Tasks`,
            card.activityId,
            styles.taskGroupTitle
          )}
          <div className={styles.taskGroupSummary}>
            <div className={styles.taskItemRow}>
              <span className={styles.taskItemLabel}>{t("activity.reward") || "Reward"}</span>
              <span className={styles.taskItemValue}>
                <img src={checkActiveIcon} alt="" className={styles.taskItemRewardIcon} />
                {formatRewardText(card, pastCurr)}
              </span>
            </div>
            <div className={styles.taskItemRow}>
              <span className={styles.taskItemLabel}>{timeRow.label}</span>
              <span className={`${styles.taskItemValue} ${styles.pastCardTimeLeftValue}`}>
                {timeRow.value}
              </span>
            </div>
          </div>
          <div className={styles.taskGroupProgressWrap}>
            <div className={styles.taskGroupProgressBar}>
              <div className={styles.taskGroupProgressFill} style={{ width: `${pct}%` }} />
              <div className={styles.taskGroupProgressDot} style={{ left: `${pct}%` }} />
            </div>
            <span className={styles.taskGroupProgressText}>{completedCount}/{totalCount}</span>
          </div>
          <div className={styles.taskGroupList}>
            {tasks.map((tk, tIdx) => {
              const done = isTaskCompleted(tk);
              return (
                <div key={tk.activityConditionId || tIdx} className={styles.taskGroupTask}>
                  <div className={styles.taskGroupTaskHead}>
                    <div className={styles.taskItemIcon}><TaskCardIcon gradientId={`ptg-${cardIdx}-${tIdx}`} /></div>
                    <span className={`${styles.taskGroupTaskTitle} ${styles.taskConditionDescriptionText}`}>
                      {tIdx + 1}. {taskConditionDisplayText(tk, t)}
                    </span>
                  </div>
                  <div className={styles.taskGroupTaskRow}>
                    {shouldShowConditionProgressRow(tk) ? (
                      <>
                        <span className={styles.taskItemLabel}>{t("activity.progress") || "Progress"}</span>
                        <span className={styles.taskGroupTaskProgress}>
                          <span className={styles.taskItemValueGreen}>{tk.progressCurrent ?? 0}</span>
                          <span className={styles.taskItemValue}>/{tk.progressTarget ?? 0} {tk.progressCurrency || ""}</span>
                        </span>
                      </>
                    ) : null}
                    <div className={styles.taskGroupTaskActions}>
                      {/* <TaskInfoIcon /> */}
                      <button type="button" className={styles.taskItemBtn} disabled>
                        {done ? (t("activity.completed") || "Completed") : (t("activity.ended") || "Ended")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className={styles.taskGroupClaimBtn}
            disabled={btnDisabled}
            onClick={() => canClaim && !claiming && handleClaimReward(card.userRewardRecordId)}
          >
            {btnLabel}
          </button>
        </article>
      );
    }

    const tk = tasks[0];

    return (
      <article key={`pc-${card.activityId}-${card.activityRewardId}-${cardIdx}`} className={`${styles.taskItem} ${styles.taskItemDisabled}`}>
        <div className={styles.taskItemHead}>
          <div className={styles.taskItemIcon}><TaskCardIcon gradientId={`ptc-${cardIdx}`} /></div>
          <div className={styles.taskItemHeadMain}>
            {renderActivityTitle(
              taskConditionDisplayText(tk, t) || card.activityName || "",
              card.activityId,
              `${styles.taskItemTitle} ${styles.taskConditionDescriptionText}`
            )}
          </div>
        </div>
        <div className={styles.taskItemRows}>
          {tk && shouldShowConditionProgressRow(tk) ? (
            <div className={styles.taskItemRow}>
              <span className={styles.taskItemLabel}>{t("activity.progress") || "Progress"}</span>
              <span className={styles.taskItemValueGreen}>
                {tk.progressCurrent ?? 0}/{tk.progressTarget ?? 0} {tk.progressCurrency || ""}
              </span>
            </div>
          ) : null}
          <div className={styles.taskItemRow}>
            <span className={styles.taskItemLabel}>{t("activity.reward") || "Reward"}</span>
            <span className={styles.taskItemValue}>
              <img src={checkActiveIcon} alt="" className={styles.taskItemRewardIcon} />
              {formatRewardText(card, pastCurr)}
            </span>
          </div>
          <div className={styles.taskItemRow}>
            <span className={styles.taskItemLabel}>{timeRow.label}</span>
            <span className={`${styles.taskItemValue} ${styles.pastCardTimeLeftValue}`}>
              {timeRow.value}
            </span>
          </div>
        </div>
        <div className={styles.taskItemFoot}>
          {/* <TaskInfoIcon /> */}
          <button
            type="button"
            className={styles.taskItemBtn}
            disabled={btnDisabled}
            onClick={() => canClaim && !claiming && handleClaimReward(card.userRewardRecordId)}
          >
            {btnLabel}
          </button>
        </div>
      </article>
    );
  };

  const renderPastAggregatedActivity = (activity, activityIdx) => {
    const rewards = activity.rewards || [];
    const claimableReward = findPastClaimableReward(activity, t, now);
    const claiming = !!claimableReward?.userRewardRecordId && !!claimingIds[claimableReward.userRewardRecordId];
    const footLabel = aggregatePastActivityFooterLabel(activity, t, now);
    const aggTimeRow = pastAggregatedTimeRow(activity, t, now, (et) => formatEndTimeToDHM(et));

    return (
      <article
        key={`pagg-${activity.activityId}-${activityIdx}`}
        className={`${styles.taskGroup} ${styles.taskItemDisabled}`}
      >
        {renderActivityTitle(activity.activityName || "", activity.activityId, styles.taskGroupTitle)}
        <div className={styles.taskGroupSummary}>
          <div className={styles.taskItemRow}>
            <span className={styles.taskItemLabel}>{aggTimeRow.label}</span>
            <span className={`${styles.taskItemValue} ${styles.pastCardTimeLeftValue}`}>
              {aggTimeRow.value}
            </span>
          </div>
          {rewards.map((reward, ri) => {
            const { sumCur, sumTgt, unit } = sumRewardConditionsProgress(reward);
            return (
              <div key={reward.activityRewardId ?? ri} className={styles.taskItemRow}>
                <span className={styles.taskItemLabel}>{t("activity.reward") || "Reward"}</span>
                <span className={styles.taskItemValue}>
                  <span className={styles.taskItemValueGreen}>{sumCur}</span>
                  <span>
                    /{sumTgt} {unit}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className={styles.taskGroupClaimBtn}
          disabled={!claimableReward || claiming}
          onClick={() => claimableReward && !claiming && handleClaimReward(claimableReward.userRewardRecordId)}
        >
          {claimableReward
            ? claiming
              ? t("activity.rewardClaimLoading") || "Loading"
              : (t("activity.claimReward") || "Claim Reward")
            : footLabel}
        </button>
      </article>
    );
  };

  const renderPastTasks = () => {
    if (!pastActivities.length) {
      return (
        <div className={styles.taskListEmpty}>
          <span className={styles.taskListEmptyText}>
            {t("activity.completeTasksEmpty") || "No tasks"}
          </span>
        </div>
      );
    }

    const nodes = [];
    pastActivities.forEach((activity, ai) => {
      if (shouldAggregatePastActivity(activity)) {
        nodes.push(renderPastAggregatedActivity(activity, ai));
        return;
      }
      const rewards = activity.rewards || [];
      if (!rewards.length) {
        const placeholderCard = {
          activityId: activity.activityId,
          activityRewardId: activity.activityId,
          activityName: activity.activityName || "",
          endTime: activity.endTime,
          timeRangeText: activity.timeRangeText,
          userStatus: activity.userStatus,
          cardType: "SINGLE_TASK",
          tasks: [],
          status: null,
          statusText: "",
          userRewardRecordId: null,
          rewardAmount: null,
          rewardCurrency: null,
          rewardDescription: activity.activityDescription || "",
          expiredAt: null,
        };
        nodes.push(renderPastRewardCard(placeholderCard, `ph-${ai}`));
        return;
      }
      rewards.forEach((reward, ri) => {
        nodes.push(
          renderPastRewardCard(activityRewardToCard(activity, reward), `p-${ai}-${ri}`)
        );
      });
    });

    return nodes;
  };

  const renderJoinActivities = () => {
    if (!joinActivitiesVisible.length) {
      return (
        <div className={styles.taskListEmpty}>
          <span className={styles.taskListEmptyText}>
            {t("activity.joinActivitiesEmpty") || "No activities"}
          </span>
        </div>
      );
    }

    return joinActivitiesVisible.map((activity) => (
      <article key={activity.activityId} className={styles.joinActivityCard}>
        <div className={styles.joinActivityCardMain}>
          <div className={styles.joinActivityCardText}>
            <h3 className={styles.joinActivityCardTitle}>
              {activity.activityName || ""}
            </h3>
            {activity.activityDescription && (
              <p className={styles.joinActivityCardDesc}>
                {activity.activityDescription}
              </p>
            )}
            {!isJoinActivityPermanent(activity) && (
              <div className={styles.joinActivityCardPill}>
                <span className={styles.joinActivityCardPillLabel}>{t("activity.endIn") || "End in"}</span>
                <span className={styles.joinActivityCardPillTime}>{getActivityTimeRemainingDisplay(activity, true)}</span>
              </div>
            )}
          </div>
          {/* <JoinActivityIcon /> */}
        </div>
        <div className={styles.joinActivityCardFooter}>
          <div className={styles.joinActivityCardActions}>
            <button
              type="button"
              className={styles.joinActivityCardBtnPrimary}
              disabled={joiningIds[activity.activityId]}
              onClick={() => handleJoinActivity(activity.activityId)}
            >
              {joiningIds[activity.activityId]
                ? (t("activity.joinLoading") || "...")
                : (t("activity.joinNow") || "Join Now")}
            </button>
            {/* <CalendarIcon /> */}
          </div>
        </div>
      </article>
    ));
  };

  const goVoucherPage = () => {
    if (typeof location !== "undefined") {
      location.href = getUrl("/activity/voucher");
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.voucherTopBar}>
        <div
          className={`${styles.summaryItem} ${styles.myVouchers}`}
          role="button"
          tabIndex={0}
          onClick={goVoucherPage}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              goVoucherPage();
            }
          }}
        >
          <div className={styles.value}>
            <span>{!loginStatus ? "—" : voucherCountLoading ? "…" : voucherTotal}</span>
            <span className={styles.labelHighlight}>{t("activity.myVouchers") || "My Vouchers"}</span>
            <ArrowRightIcon />
          </div>
          <div className={styles.label}>
            <VouchersIcon />
            <span>{t("activity.vouchers") || "Vouchers"}</span>
          </div>
        </div>
        <img src={voucherIcon} alt="" className={styles.voucherIcon} />
      </div>

      {/*<div className={styles.summary}>*/}
      {/*  <div className={styles.summaryItem}>*/}
      {/*    <div className={styles.value}>27200</div>*/}
      {/*    <div className={styles.label}>*/}
      {/*      <PointsIcon />*/}
      {/*      <span>{t("activity.points") || "Points"}</span>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</div>*/}

      {/*<div className={styles.card}>*/}
      {/*  <div className={styles.cardTitle}>{t("activity.dailyCheckIn") || "Daily Check-In"}</div>*/}
      {/*  <div className={styles.cardMeta}>*/}
      {/*    <span>2026-03-02 ~ 2026-03-08</span>*/}
      {/*    <span className={styles.ruleLinkWrap} onClick={() => setRuleDrawerVisible(true)}>*/}
      {/*      <span className={styles.ruleLink}>{t("activity.rule") || "Rule"}</span>*/}
      {/*      <RuleArrowIcon />*/}
      {/*    </span>*/}
      {/*  </div>*/}
      {/*  <div className={styles.weekRow}>*/}
      {/*    {WEEK_DAYS.map((day, i) => {*/}
      {/*      const isActive = i === 0;*/}
      {/*      return (*/}
      {/*        <div key={day} className={styles.dayCell}>*/}
      {/*          <div className={`${styles.dayBox} ${isActive ? styles.dayBoxActive : ""}`}>*/}
      {/*            <img*/}
      {/*              src={isActive ? checkActiveIcon : checkIcon}*/}
      {/*              alt=""*/}
      {/*              className={styles.dayBoxIcon}*/}
      {/*            />*/}
      {/*            <span className={styles.dayPoints}>+1 to 10</span>*/}
      {/*          </div>*/}
      {/*          <span className={`${styles.dayName} ${isActive ? styles.dayNameActive : ""}`}>{day}</span>*/}
      {/*        </div>*/}
      {/*      );*/}
      {/*    })}*/}
      {/*  </div>*/}
      {/*  <button type="button" className={styles.checkInBtn}>*/}
      {/*    {t("activity.checkIn") || "Check In"}*/}
      {/*  </button>*/}
      {/*  <div className={styles.checkInDescWrap}>*/}
      {/*    <p className={styles.checkInDesc}>*/}
      {/*      {t("activity.checkInEveryday") || "Check in every day for a week (Mon-Sun) by spending at least 2 minutes on the App in one single session to win points."}*/}
      {/*    </p>*/}
      {/*    <CheckInDescArrowIcon />*/}
      {/*  </div>*/}
      {/*</div>*/}

      <div className={styles.taskSection}>
        {/* 一级 tab：Complete Tasks | Join Activities */}
        <div className={styles.tabsPrimary}>
          <span
            className={primaryTab === "completeTasks" ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setPrimaryTab("completeTasks")}
          >
            {t("activity.completeTasks") || "Complete Tasks"}
          </span>
          <span
            className={primaryTab === "joinActivities" ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setPrimaryTab("joinActivities")}
          >
            {t("activity.joinActivities") || "Join Activities"}
          </span>
        </div>

        {primaryTab === "completeTasks" && (
          <>
            {/* 二级 tab：Ongoing | Past */}
            <div className={styles.tabsSecondary}>
              <span
                className={secondaryTab === "ongoing" ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => setSecondaryTab("ongoing")}
              >
                {t("activity.ongoing") || "Ongoing"}
              </span>
              <span
                className={secondaryTab === "past" ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => setSecondaryTab("past")}
              >
                {t("activity.past") || "Past"}
              </span>
            </div>

            {secondaryTab === "ongoing" && (
              <div className={styles.taskList}>
                {renderOngoingTasks()}
              </div>
            )}
            {secondaryTab === "past" && (
              <div className={styles.taskList}>
                {renderPastTasks()}
              </div>
            )}
          </>
        )}

        {primaryTab === "joinActivities" && (
          <div className={styles.taskList}>
            {renderJoinActivities()}
          </div>
        )}
      </div>

      <Drawer
        title={t("activity.checkInRuleTitle") || "Daily Check-In Rule"}
        placement="bottom"
        height="auto"
        open={ruleDrawerVisible}
        onClose={() => setRuleDrawerVisible(false)}
        className={styles.ruleDrawer}
      >
        {/* 规则正文待补充 */}
      </Drawer>
    </div>
  );
}
